import type { CSSProperties } from 'react';

// Pencil marks are stretched SVG paths. A non-scaling stroke keeps the line weight even on boxes of any shape.
const RING = 'M14 10 C 30 1, 74 0, 92 9 C 101 14, 100 27, 88 33 C 70 41, 26 41, 10 33 C 0 27, 0 15, 10 10 C 16 7, 24 5, 34 4';
const BRACKET = 'M1 10 C 1 4, 2 2, 6 2 L 94 2 C 98 2, 99 4, 99 10';

export type Ink = 'blue' | 'red';

/** A loose ring around its positioned parent. Blue marks template habits, red marks usability problems, and a dotted ring marks a supporting signal. */
export function Ring({ ink = 'blue', dotted, className = '', style }: { ink?: Ink; dotted?: boolean; className?: string; style?: CSSProperties }) {
  return <svg className={`pencil pencil-ring ink-${ink}${dotted ? ' dotted' : ''} ${className}`} viewBox="0 0 100 40" preserveAspectRatio="none" aria-hidden="true" focusable="false" style={style}>
    <path d={RING} pathLength={dotted ? undefined : 1} vectorEffect="non-scaling-stroke" />
  </svg>;
}

/** A bracket over a run of repeated elements. */
export function Bracket({ ink = 'blue' }: { ink?: Ink }) {
  return <svg className={`pencil bracket ink-${ink}`} viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true" focusable="false"><path d={BRACKET} pathLength={1} vectorEffect="non-scaling-stroke" /></svg>;
}

/** A loose pencil line struck through stock copy. */
export function Strike({ ink = 'blue' }: { ink?: Ink }) {
  return <svg className={`pencil strike ink-${ink}`} viewBox="0 0 100 10" preserveAspectRatio="none" aria-hidden="true" focusable="false"><path d="M1 6 C 20 3, 45 7, 66 4 C 80 2, 92 5, 99 3" pathLength={1} vectorEffect="non-scaling-stroke" /></svg>;
}

/** The wordmark's loop and the small ink samples beside headings. */
export function Loop({ ink = 'blue', dotted, size = 26 }: { ink?: Ink; dotted?: boolean; size?: number }) {
  return <svg className={`pencil loop ink-${ink}${dotted ? ' dotted' : ''}`} width={size} height={Math.round(size * 0.84)} viewBox="0 0 100 84" aria-hidden="true" focusable="false"><path d="M14 50 C 8 22, 40 6, 66 10 C 92 14, 98 40, 88 58 C 76 78, 36 82, 18 68 C 6 58, 6 38, 22 26" /></svg>;
}

/** A pencil tick for finished steps. */
export function Tick() {
  return <svg className="pencil tick" width="22" height="22" viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M4 13 C 6 15, 8 17, 9.5 19 C 12 13, 16 8, 21 4" /></svg>;
}

/** A circled note number. */
export function NoteNumber({ n, ink = 'blue', filled }: { n: number; ink?: Ink; filled?: boolean }) {
  return <span className={`note-number ink-${ink}${filled ? ' filled' : ''}`} aria-hidden="true">{n}</span>;
}
